import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Theme,
} from '@mui/material';
import { MouseEvent, useState } from 'react';

import { CustomInputProps } from './types';
import { FieldError } from '../FieldError/FieldError';

export const CustomInput: React.FC<CustomInputProps> = ({
  label,
  error,
  isPassword,
  onChange,
  value,
  fieldName,
  autoComplete,
  shortInput = false,
  marginBottom = true,
  styles,
  variant = 'standard',
  multiline = false,
  maxRows = 1,
  type,
  inputMode,
  pattern,
  enterKeyHint,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () =>
    setShowPassword((show: boolean) => !show);

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  return (
    <Stack
      alignItems="center"
      sx={(theme: Theme) => ({
        display: 'grid',
        position: 'relative',
        borderBottom: `2px solid ${theme.palette.gray.main}`,
        paddingBottom: shortInput ? '4px' : '10px',
        marginBottom: { xs: shortInput ? 0 : marginBottom ? '20px' : '0' },
        ...styles,
      })}
    >
      <TextField
        variant={variant}
        name={fieldName}
        autoComplete={autoComplete}
        placeholder={label}
        value={value}
        onChange={onChange}
        type={isPassword ? (showPassword ? 'text' : 'password') : (type ?? 'text')}
        multiline={multiline}
        maxRows={maxRows}
        // `inputMode` is what picks the on-screen keyboard on iOS, and it has
        // to reach the <input> itself rather than the MUI wrapper — a field
        // that says nothing here gets the full qwerty keyboard whatever it
        // actually holds.
        slotProps={{
          htmlInput: { inputMode, pattern, enterKeyHint },
          ...(isPassword
            ? {
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => handleClickShowPassword()}
                        onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => handleMouseDownPassword(e)}
                        onMouseUp={(e: React.MouseEvent<HTMLButtonElement>) => handleMouseUpPassword(e)}
                        edge="end"
                        sx={{ paddingRight: { xs: '16px', marginRight: 0 } }}
                      >
                        {showPassword ? (
                          <VisibilityOff
                            sx={(theme: Theme) => ({
                              color: theme.palette.green.main,
                            })}
                          />
                        ) : (
                          <Visibility
                            sx={(theme: Theme) => ({
                              color: theme.palette.green.main,
                            })}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }
            : {}),
        }}
        sx={(theme: Theme) => ({
          '& .MuiInputBase-root.MuiInput-root': {
            color: shortInput
              ? theme.palette.green.main
              : theme.palette.gray.main,
            width: '100%',
            caretColor: theme.palette.green.main,
            '& .MuiInputBase-input': {
              padding: 0,
              fontSize: shortInput ? '20px' : '16px',
              textAlign: shortInput ? 'center' : 'left',
              fontWeight: 400,
              outline: 'none',
            },
          },
          '& .MuiInputBase-root.MuiInput-root:before': {
            borderBottom: 'none',
          },
          '& .MuiInputBase-root.MuiInput-root:after': {
            borderBottom: 'none',
          },
          '& .MuiInputBase-root.MuiInput-root:hover:before': {
            borderBottom: 'none',
          },
        })}
      />
      <FieldError error={error} />
    </Stack>
  );
};
